import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiWithAuth } from '../lib/api';
import toast from 'react-hot-toast';
import {
  HeartIcon,
  MapPinIcon,
  UserIcon,
  CalendarIcon,
  ChevronRightIcon,
  TrashIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid';
import { API_BASE } from '../lib/config';

const API = `${API_BASE}`;

const FavoritesPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    if (user.user_type !== 'job_seeker') {
      navigate('/');
      return;
    }

    fetchFavorites();
  }, [user, navigate]);

  const fetchFavorites = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const authApi = apiWithAuth(token);
      const demo = (process.env.REACT_APP_DEMO_MODE || '').toString().toLowerCase();
      const qs = (demo === '1' || demo === 'true' || demo === 'on') ? '?demo=1' : '';
      const response = await authApi.get(`/employer-favorites${qs}`);
      setFavorites(response.data);

      console.log('Favorites fetched successfully:', response.data?.length);
    } catch (error) {
      console.error('Failed to fetch favorites:', error);
      toast.error('お気に入りの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const toggleFavorite = async (employerId) => {
    try {
      const token = localStorage.getItem('token');
      const authApi = apiWithAuth(token);
      const demo = (process.env.REACT_APP_DEMO_MODE || '').toString().toLowerCase();
      const qs = (demo === '1' || demo === 'true' || demo === 'on') ? '?demo=1' : '';
      const response = await authApi.post(`/employer-favorites/${employerId}${qs}`, {});
      
      toast.success(response.data.message);
      await fetchFavorites(); // リフレッシュ
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
      toast.error('お気に入りの更新に失敗しました');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">読み込み中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <button
                onClick={() => navigate(-1)}
                className="mr-4 p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
              >
                <ArrowLeftIcon className="h-6 w-6" />
              </button>
              <h1 className="text-2xl font-bold text-gray-900">お気に入り事業者</h1>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">
                {favorites.length}件のお気に入り
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {favorites.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <HeartIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">お気に入り事業者がありません</h3>
            <p className="text-gray-600 mb-6">気になる事業者をお気に入りに登録しましょう。</p>
            <Link
              to="/mobile-search"
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
            >
              事業者を探す
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {favorites.map((favorite) => (
              <FavoriteCard
                key={favorite.id}
                favorite={favorite}
                onToggleFavorite={toggleFavorite}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Favorite Card Component
const FavoriteCard = ({ favorite, onToggleFavorite }) => {
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
      {/* サムネイル画像 */}
      <div className="h-48 bg-gray-200 relative">
        {favorite.employer_thumbnail ? (
          <img 
            src={favorite.employer_thumbnail} 
            alt={favorite.employer_name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-gray-300 to-gray-400 flex items-center justify-center">
            <UserIcon className="w-16 h-16 text-gray-500" />
          </div>
        )}
        
        {/* お気に入り解除ボタン */}
        <button
          onClick={() => onToggleFavorite(favorite.employer_id)}
          className="absolute top-3 right-3 p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors shadow-lg"
          title="お気に入りから削除"
        >
          <HeartIconSolid className="w-5 h-5" />
        </button>
      </div>
      
      <div className="p-6">
        {/* 事業者名 */}
        <h3 className="text-xl font-bold text-gray-900 mb-2">
          {favorite.employer_name}
        </h3>
        
        {/* 事業者の紹介文（3行まで） */}
        {favorite.employer_bio && (
          <p className="text-gray-600 mb-3 text-sm overflow-hidden" style={{
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical'
          }}>
            {favorite.employer_bio}
          </p>
        )}
        
        {/* 勤務地 */}
        <div className="flex items-center text-sm text-gray-500 mb-2">
          <MapPinIcon className="w-4 h-4 mr-2 flex-shrink-0" />
          <span>
            {favorite.employer_prefecture}{favorite.employer_city && ` ${favorite.employer_city}`}
          </span>
        </div>
        
        {/* お気に入り登録日 */}
        <div className="flex items-center text-sm text-gray-500 mb-4">
          <CalendarIcon className="w-4 h-4 mr-2 flex-shrink-0" />
          <span>お気に入り登録: {formatDate(favorite.created_at)}</span>
        </div>
        
        {/* 詳細リンクボタン */}
        <Link
          to={`/employer-profile/${favorite.employer_id}`}
          className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center justify-center"
        >
          詳細を見る
          <ChevronRightIcon className="w-4 h-4 ml-2" />
        </Link>
      </div>
    </div>
  );
};

export default FavoritesPage;